import axios from "axios";
import { toast } from "react-toastify";

// Error types
export const ERROR_TYPES = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  AUTH_ERROR: 'AUTH_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  NOT_FOUND_ERROR: 'NOT_FOUND_ERROR'
};

// Custom error class
export class ApiError extends Error {
  constructor(message, type, statusCode, validationErrors = null) {
    super(message);
    this.name = 'ApiError';
    this.type = type;
    this.statusCode = statusCode;
    this.validationErrors = validationErrors;
  }
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api',
  headers: {
    "Content-Type": "application/json",
  },
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Enhanced response interceptor with refresh token handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors
    if (!error.response) {
      const networkError = new ApiError(
        "Network error. Please check your connection.",
        ERROR_TYPES.NETWORK_ERROR,
        null
      );
      toast.error(networkError.message);
      return Promise.reject(networkError);
    }

    const { status, data } = error.response;

    // Handle 401 with refresh token
    if (status === 401 && !originalRequest._retry) {
      // Don't try to refresh for login/refresh endpoints
      if (originalRequest.url?.includes('/login') || originalRequest.url?.includes('/refresh')) {
        return handleAuthError(data);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("refreshToken");

      if (refreshToken) {
        try {
          const response = await axios.post(
            `${api.defaults.baseURL}/auth/refresh`,
            { refreshToken }
          );

          const { token: newToken } = response.data;
          localStorage.setItem("token", newToken);

          api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

          processQueue(null, newToken);

          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          clearAuthAndRedirect();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        clearAuthAndRedirect();
        return handleAuthError(data);
      }
    }

    // Handle other errors
    let apiError;

    switch (status) {
      case 400:
        apiError = new ApiError(
          data?.message || "Invalid request",
          ERROR_TYPES.VALIDATION_ERROR,
          status,
          data?.validationErrors
        );
        break;

      case 401:
        return handleAuthError(data);

      case 404:
        apiError = new ApiError(
          data?.message || "Resource not found",
          ERROR_TYPES.NOT_FOUND_ERROR,
          status
        );
        break;

      case 500:
      case 502:
      case 503:
        apiError = new ApiError(
          data?.message || "Server error. Please try again later.",
          ERROR_TYPES.SERVER_ERROR,
          status
        );
        break;

      default:
        apiError = new ApiError(
          data?.message || "An error occurred",
          ERROR_TYPES.SERVER_ERROR,
          status
        );
    }

    // Show toast for non-auth errors
    if (apiError.type !== ERROR_TYPES.AUTH_ERROR) {
      toast.error(apiError.message);
    }

    return Promise.reject(apiError);
  }
);

function handleAuthError(data) {
  const apiError = new ApiError(
    data?.message || "Authentication required",
    ERROR_TYPES.AUTH_ERROR,
    401
  );
  clearAuthAndRedirect();
  return Promise.reject(apiError);
}

function clearAuthAndRedirect() {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("userId");
  localStorage.removeItem("userType");
  localStorage.removeItem("userName");
  if (!window.location.pathname.includes('/login')) {
    window.location.href = "/login";
  }
}

// Logout function that revokes refresh token
export const logout = async () => {
  const refreshToken = localStorage.getItem("refreshToken");

  if (refreshToken) {
    try {
      await api.post('/auth/logout', { refreshToken });
    } catch (error) {
      console.error("Logout error:", error);
    }
  }

  clearAuthAndRedirect();
};

export default api;
